import React, { FC, useEffect, useState } from "react";
import {
  Form, Input, Switch, message, Spin, Table, Tag, Empty,
} from "antd";
import { PlusOutlined, ArrowLeftOutlined, EditOutlined, UserOutlined } from "@ant-design/icons";
import { BASE_URL } from "../../../constants/urlConstants";
import { toast,toastConfirm } from "../../../utils/toast";
import "./AddME.css";

const { TextArea } = Input;

const AddME: FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading]               = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [data, setData]                     = useState<any[]>([]);
  const [showForm, setShowForm]             = useState(false);
  const [editingId, setEditingId]           = useState<number | null>(null);

  /* ── FETCH ── */
  const fetchMEs = async () => {
    try {
      setInitialLoading(true);
      const res = await fetch(`${BASE_URL}/api/finalle/employees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roleType: 2 }),
      });
      if (!res.ok) throw new Error("Failed to fetch MEs");
      const result = await res.json();
      if (!Array.isArray(result)) throw new Error("Invalid response");
      setData(result);
    } catch (err: any) { toast("error",err.message); }
    finally { setInitialLoading(false); }
  };

  useEffect(() => { fetchMEs(); }, []);

  /* ── SUBMIT ── */
  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        roleType: 2,
        mobile: values.mobile,
        username: values.username,
        password: values.password,
        email: values.email,
        address: values.address,
        area: values.area,
        status: values.status ?? true,
        pincodes: values.pincodes
          ? values.pincodes.split(",").map((p: string) => Number(p.trim()))
          : [],
        distributorIds: values.distributorIds
          ? values.distributorIds.split(",").map((d: string) => d.trim())
          : [],
      };
      const isEdit = !!editingId;
      const url = isEdit
        ? `${BASE_URL}/api/finalle/employee/update/${editingId}`
        : `${BASE_URL}/api/finalle/employee`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(isEdit ? "Update failed" : "Creation failed");
      toast("success",isEdit ? "Marketing Executive updated!" : "Marketing Executive added!");
      form.resetFields(); setShowForm(false); setEditingId(null); fetchMEs();
    } catch (err: any) { toast("error", err.message); }
    finally { setLoading(false); }
  };

  /* ── EDIT ── */
  const handleEdit = (record: any) => {
    setEditingId(record.id); setShowForm(true);
    form.setFieldsValue({
      username: record.username,
      mobile: record.mobile,
      email: record.email,
      password: "",
      address: record.address,
      area: record.area,
      status: record.status,
      pincodes: record.pincodes?.join(", "),
      distributorIds: record.distributorIds?.join(", "),
    });
  };

  const handleCancel = () => { setShowForm(false); setEditingId(null); form.resetFields(); };

  /* ── TABLE COLUMNS (desktop) ── */
  const columns = [
    { title: "Emp ID",  dataIndex: "empId",    width: 100 },
    { title: "Name",    dataIndex: "username"              },
    { title: "Mobile",  dataIndex: "mobile",   width: 120 },
    { title: "Area",    dataIndex: "area"                  },
    {
      title: "Pincodes",
      render: (r: any) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {r.pincodes?.map((p: string) => (
            <Tag key={p} style={{ borderRadius: 2, fontSize: 11, fontWeight: 600, margin: 0 }}>{p}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Status", width: 90,
      render: (r: any) => r.status
        ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Active</Tag>
        : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11 }}>Inactive</Tag>,
    },
    {
      title: "Action", width: 80,
      render: (r: any) => (
        <button className="me-edit-btn" onClick={() => handleEdit(r)}>
          <EditOutlined /> Edit
        </button>
      ),
    },
  ];

  if (initialLoading) return (
    <div className="me-loader"><Spin size="large" /><p>Loading executives…</p></div>
  );

  return (
    <div className="me-page">

      {/* ── Top bar ── */}
      <div className="me-topbar">
        <div className="me-topbar-left">
          <UserOutlined className="me-topbar-icon" />
          <span className="me-topbar-title">Marketing Executives</span>
          {data.length > 0 && <span className="me-topbar-badge">{data.length} total</span>}
        </div>
        {!showForm && (
          <button className="me-add-btn" onClick={() => setShowForm(true)}>
            <PlusOutlined /> Add ME
          </button>
        )}
      </div>

      {/* ── Breadcrumb ── */}
      <div className="me-breadcrumb">
        Dashboard &rsaquo; Marketing Executives{showForm && <> &rsaquo; {editingId ? "Edit" : "Add New"}</>}
      </div>

      {/* ══ FORM VIEW ══ */}
      {showForm ? (
        <div className="me-form-body">
          <button className="me-back-btn" onClick={handleCancel}>
            <ArrowLeftOutlined /> Back
          </button>

          <div className="me-form-card">
            <div className="me-form-card-header">
              <UserOutlined />
              <span>{editingId ? "Edit Marketing Executive" : "New Marketing Executive"}</span>
              {editingId && <span className="me-form-id-badge">#{editingId}</span>}
            </div>

            <div className="me-form-card-body">
              <Form form={form} layout="vertical" onFinish={onFinish}
                initialValues={{ status: true }}>

                {/* ── Section: Account ── */}
                <div className="me-section-label">Account Details</div>

                <div className="me-form-row">
                  <Form.Item name="username" label="Username" rules={[{ required: true, message: "Required" }]}>
                    <Input size="large" placeholder="Full name" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label="Password"
                    rules={[{ required: !editingId, message: "Password required" }]}
                  >
                    <Input.Password
                      size="large"
                      placeholder={editingId ? "Leave blank to keep same" : "Set password"}
                    />
                  </Form.Item>
                </div>

                <div className="me-form-row">
                  <Form.Item name="mobile" label="Mobile"
                    rules={[{ required: true }, { pattern: /^\d{10}$/, message: "10 digits" }]}>
                    <Input size="large" maxLength={10} placeholder="10-digit number" />
                  </Form.Item>
                  <Form.Item name="email" label="Email"
                    rules={[{ required: true }, { type: "email", message: "Valid email" }]}>
                    <Input size="large" placeholder="email@example.com" />
                  </Form.Item>
                </div>

                {/* ── Section: Location ── */}
                <div className="me-section-label">Location & Coverage</div>

                <Form.Item name="address" label="Address" rules={[{ required: true, message: "Required" }]}>
                  <TextArea rows={3} placeholder="Full address" />
                </Form.Item>

                <div className="me-form-row">
                  <Form.Item name="area" label="Area" rules={[{ required: true, message: "Required" }]}>
                    <Input size="large" placeholder="e.g. Kurnool South" />
                  </Form.Item>
                  <Form.Item name="pincodes" label="Pincodes" rules={[{ required: true, message: "Required" }]}>
                    <Input size="large" placeholder="518002, 518003" />
                  </Form.Item>
                </div>

                <Form.Item name="distributorIds" label="Distributor IDs">
                  <Input size="large" placeholder="E20261001, E20261002 (comma separated)" />
                </Form.Item>

                {/* ── Section: Status ── */}
                <div className="me-section-label">Status</div>

                <div className="me-switch-item">
                  <Form.Item name="status" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                  <div>
                    <p className="me-switch-label">Active Status</p>
                    <p className="me-switch-hint">Enable or disable this marketing executive</p>
                  </div>
                </div>

                {/* ── Actions ── */}
                <div className="me-form-actions">
                  <button type="submit" className={`me-submit-btn${loading ? " loading" : ""}`} disabled={loading}>
                    {loading ? "Saving…" : editingId ? "Update ME" : "Add ME"}
                  </button>
                  <button type="button" className="me-cancel-btn" onClick={handleCancel}>Cancel</button>
                </div>
              </Form>
            </div>
          </div>
        </div>

      ) : data.length === 0 ? (
        /* ══ EMPTY ══ */
        <div className="me-empty">
          <Empty description={<span className="me-empty-text">No Marketing Executives found.</span>} />
          <button className="me-add-btn" style={{ marginTop: 20 }} onClick={() => setShowForm(true)}>
            <PlusOutlined /> Add ME
          </button>
        </div>

      ) : (
        /* ══ LIST VIEW ══ */
        <div className="me-table-wrap">

          {/* Desktop table */}
          <div className="me-table-desktop">
            <Table dataSource={data} columns={columns} rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              className="me-table" rowClassName={() => "me-table-row"}
              scroll={{ x: true }} />
          </div>

          {/* Mobile card list */}
          <div className="me-table-mobile">
            <div className="me-card-list">
              {data.map((r: any) => (
                <div key={r.id} className="me-dist-card">
                  <div className="me-dist-card-header">
                    <span className="me-dist-card-id">{r.empId}</span>
                    <span className="me-dist-card-name">{r.username}</span>
                    {r.status
                      ? <Tag color="success" style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Active</Tag>
                      : <Tag color="error"   style={{ borderRadius: 2, fontWeight: 600, fontSize: 11, margin: 0 }}>Inactive</Tag>
                    }
                  </div>
                  <div className="me-dist-card-body">
                    <div className="me-dist-card-row">
                      <span className="me-dist-card-row-label">Mobile</span>
                      <span>{r.mobile}</span>
                    </div>
                    <div className="me-dist-card-row">
                      <span className="me-dist-card-row-label">Area</span>
                      <span>{r.area}</span>
                    </div>
                    <div className="me-dist-card-row">
                      <span className="me-dist-card-row-label">Pincodes</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {r.pincodes?.map((p: string) => (
                          <Tag key={p} style={{ borderRadius: 2, fontSize: 11, fontWeight: 600, margin: 0 }}>{p}</Tag>
                        ))}
                      </div>
                    </div>
                    {r.distributorIds?.length > 0 && (
                      <div className="me-dist-card-row">
                        <span className="me-dist-card-row-label">Dists</span>
                        <span style={{ fontSize: 12, color: "#555" }}>{r.distributorIds.join(", ")}</span>
                      </div>
                    )}
                  </div>
                  <div className="me-dist-card-footer">
                    <button className="me-edit-btn" onClick={() => handleEdit(r)}>
                      <EditOutlined /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default AddME;